import React, { useState, useEffect } from 'react';

const OtelDataDictionary = ({ entities = {} }) => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedNodes, setExpandedNodes] = useState(new Set());

    // OTEL-compatible schema with semantic conventions
    const otelSchema = {
        // Trace semantic conventions
        trace: {
            attributes: {
                'service.name': 'cyber-intel-agent',
                'service.version': '1.0.0',
                'deployment.environment': 'production',
                'telemetry.sdk.name': 'opentelemetry',
                'telemetry.sdk.language': 'javascript'
            }
        },
        // Resource semantic conventions
        resource: {
            'cloud.provider': 'docker',
            'container.name': 'agentic-frontend',
            'host.name': 'localhost',
            'process.runtime.name': 'node',
            'process.runtime.version': '18.x'
        },
        // Entity mappings with OTEL attributes
        entities: {
            Document: {
                category: 'document',
                otelAttributes: {
                    'document.id': 'string',
                    'document.title': 'string',
                    'document.content_type': 'string',
                    'document.processing_time_ms': 'number',
                    'document.entity_count': 'number'
                },
                fields: {
                    id: { type: 'ID', required: true, otelKey: 'document.id' },
                    title: { type: 'String', required: true, otelKey: 'document.title' },
                    content: { type: 'String', required: true },
                    filename: { type: 'String', otelKey: 'document.filename' },
                    contentType: { type: 'String', otelKey: 'document.content_type' },
                    createdAt: { type: 'String', required: true, otelKey: 'document.created_at' },
                    processingTime: { type: 'Float', otelKey: 'document.processing_time_ms' }
                },
                metrics: ['document.processing_time_ms', 'document.entity_count']
            },
            Person: {
                category: 'entity',
                otelAttributes: {
                    'entity.type': 'person',
                    'entity.name': 'string',
                    'entity.confidence': 'number'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    name: { type: 'String', required: true, otelKey: 'entity.name' },
                    confidence: { type: 'Float', otelKey: 'entity.confidence' }
                }
            },
            ThreatActor: {
                category: 'threat',
                otelAttributes: {
                    'threat.actor.name': 'string',
                    'threat.actor.aliases': 'string[]',
                    'threat.actor.confidence': 'number',
                    'threat.severity': 'string'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    name: { type: 'String', required: true, otelKey: 'threat.actor.name' },
                    aliases: { type: '[String]', otelKey: 'threat.actor.aliases' },
                    confidence: { type: 'Float', otelKey: 'threat.actor.confidence' }
                },
                spans: ['threat_detection', 'threat_analysis']
            },
            MitreTactic: {
                category: 'framework',
                otelAttributes: {
                    'mitre.tactic.id': 'string',
                    'mitre.tactic.name': 'string',
                    'mitre.framework.version': 'string'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    tacticId: { type: 'String', required: true, otelKey: 'mitre.tactic.id' },
                    name: { type: 'String', required: true, otelKey: 'mitre.tactic.name' },
                    description: { type: 'String' }
                }
            },
            MitreTechnique: {
                category: 'framework',
                otelAttributes: {
                    'mitre.technique.id': 'string',
                    'mitre.technique.name': 'string',
                    'mitre.technique.tactic_id': 'string'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    techniqueId: { type: 'String', required: true, otelKey: 'mitre.technique.id' },
                    name: { type: 'String', required: true, otelKey: 'mitre.technique.name' },
                    tacticId: { type: 'String', otelKey: 'mitre.technique.tactic_id' },
                    description: { type: 'String' }
                }
            },
            CVE: {
                category: 'vulnerability',
                otelAttributes: {
                    'vulnerability.cve.id': 'string',
                    'vulnerability.cvss.score': 'number',
                    'vulnerability.severity': 'string'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    cveId: { type: 'String', required: true, otelKey: 'vulnerability.cve.id' },
                    description: { type: 'String' },
                    cvssScore: { type: 'Float', otelKey: 'vulnerability.cvss.score' },
                    severity: { type: 'String', otelKey: 'vulnerability.severity' }
                }
            },
            Country: {
                category: 'location',
                otelAttributes: {
                    'geo.country.name': 'string',
                    'geo.country.iso_code': 'string'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    name: { type: 'String', required: true, otelKey: 'geo.country.name' },
                    code: { type: 'String', otelKey: 'geo.country.iso_code' },
                    confidence: { type: 'Float' }
                }
            },
            System: {
                category: 'infrastructure',
                otelAttributes: {
                    'system.name': 'string',
                    'system.type': 'string',
                    'system.version': 'string'
                },
                fields: {
                    id: { type: 'ID', required: true },
                    name: { type: 'String', required: true, otelKey: 'system.name' },
                    type: { type: 'String', otelKey: 'system.type' },
                    version: { type: 'String', otelKey: 'system.version' },
                    confidence: { type: 'Float' }
                }
            }
        }
    };

    const categories = {
        all: 'All Entities',
        document: 'Documents',
        entity: 'Entities',
        threat: 'Threats',
        framework: 'Frameworks',
        vulnerability: 'Vulnerabilities',
        location: 'Locations',
        infrastructure: 'Infrastructure'
    };

    const toggleNode = (nodeId) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    const renderField = (field, value) => {
        return (
            <div className="ms-3 mb-1">
                <span className="text-muted">Type:</span> <code>{field.type}</code>
                {field.required && <span className="badge bg-danger ms-2">Required</span>}
                {field.otelKey && (
                    <div className="mt-1">
                        <span className="text-muted">OTEL Key:</span> 
                        <code className="ms-2">{field.otelKey}</code>
                    </div>
                )}
            </div>
        );
    };

    const renderEntity = (entityName, entityDef) => {
        const isExpanded = expandedNodes.has(entityName);
        const matchesSearch = !searchTerm || 
            entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            Object.keys(entityDef.fields).some(f => f.toLowerCase().includes(searchTerm.toLowerCase()));

        if (!matchesSearch) return null;
        if (selectedCategory !== 'all' && entityDef.category !== selectedCategory) return null;

        return (
            <div key={entityName} className="card mb-3">
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => toggleNode(entityName)}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                            <i className={`fas fa-${isExpanded ? 'minus' : 'plus'}-circle me-2`}></i>
                            {entityName}
                            <span className="badge bg-secondary ms-2">{entityDef.category}</span>
                        </h6>
                        <small className="text-muted">
                            {Object.keys(entityDef.fields).length} fields
                        </small>
                    </div>
                </div>
                {isExpanded && (
                    <div className="card-body">
                        {/* OTEL Attributes */}
                        <div className="mb-3">
                            <h6 className="text-primary">OTEL Attributes</h6>
                            <div className="bg-light p-2 rounded">
                                {Object.entries(entityDef.otelAttributes).map(([key, type]) => (
                                    <div key={key}>
                                        <code>{key}</code>: <span className="text-muted">{type}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Fields */}
                        <div className="mb-3">
                            <h6 className="text-primary">Fields</h6>
                            {Object.entries(entityDef.fields).map(([fieldName, field]) => (
                                <div key={fieldName} className="border-start border-2 ps-3 mb-2">
                                    <strong>{fieldName}</strong>
                                    {renderField(field)}
                                </div>
                            ))}
                        </div>

                        {/* Metrics */}
                        {entityDef.metrics && (
                            <div className="mb-3">
                                <h6 className="text-primary">OTEL Metrics</h6>
                                <ul className="list-unstyled">
                                    {entityDef.metrics.map(metric => (
                                        <li key={metric}><code>{metric}</code></li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Spans */}
                        {entityDef.spans && (
                            <div>
                                <h6 className="text-primary">OTEL Spans</h6>
                                <ul className="list-unstyled">
                                    {entityDef.spans.map(span => (
                                        <li key={span}><code>{span}</code></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="otel-data-dictionary">
            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">
                        <i className="fas fa-book me-2"></i>
                        OTEL-Compatible Data Dictionary
                    </h5>
                </div>
                <div className="card-body">
                    {/* Controls */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search entities or fields..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <select 
                                className="form-select"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                {Object.entries(categories).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* OTEL Configuration */}
                    <div className="alert alert-info mb-3">
                        <h6><i className="fas fa-info-circle me-2"></i>OpenTelemetry Configuration</h6>
                        <small>
                            <strong>Service:</strong> {otelSchema.trace.attributes['service.name']} v{otelSchema.trace.attributes['service.version']}<br/>
                            <strong>Environment:</strong> {otelSchema.trace.attributes['deployment.environment']}<br/>
                            <strong>SDK:</strong> {otelSchema.trace.attributes['telemetry.sdk.name']} ({otelSchema.trace.attributes['telemetry.sdk.language']})
                        </small>
                    </div>

                    {/* Entity List */}
                    <div className="entity-list">
                        {Object.entries(otelSchema.entities).map(([name, def]) => 
                            renderEntity(name, def)
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OtelDataDictionary;
